import React, { useMemo, useState } from "react";
import styled, { useTheme } from "styled-components";
import { Widget, tokens } from "../../activity-logs/styles";
import { IOS } from "../../activity-logs/labels";
import { PeriodStats } from "../../creditos/styles";
import { hairline, surface } from "../../../styles/designTokens";
import { buildYearCascade, money } from "./priceData";

/**
 * Para onde vai cada real do preço, em tabela. Compara hoje com o ano escolhido, e cada
 * linha traz a própria trajetória de 2026 a 2033. Apontar um ano, na régua ou em uma
 * minicurva, mostra esse ano na tabela inteira; clicar fixa a escolha.
 */

const label2 = ({ theme }) => tokens.label2(theme);
const THIS_YEAR = new Date().getFullYear();
const COLUMNS = "minmax(150px, 1.2fr) minmax(112px, 1fr) minmax(112px, 1fr) minmax(100px, 0.9fr) minmax(76px, 0.7fr)";

const pct = (v) => `${v <= -0.05 ? "−" : ""}${Math.abs(v).toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%`;
const signedMoney = (v) => (Math.abs(v) < 0.005 ? "—" : `${v > 0 ? "+" : "−"}${money(Math.abs(v))}`);
const plainMoney = (v) => (v < 0 ? `−${money(Math.abs(v))}` : money(v));

const ROWS = [
  { key: "price", label: "Preço de venda", kind: "total" },
  { key: "cost", label: "Custo total (com frete)", kind: "fixed" },
  { key: "gross", label: "Sobra depois do custo", kind: "subtotal" },
  { key: "oldNet", label: "Tributos que saem", color: IOS.blue, kind: "step" },
  { key: "newDebit", label: "Débito de CBS + IBS", color: IOS.red, kind: "step" },
  { key: "creditCost", label: "Crédito sobre o custo", color: IOS.green, kind: "step" },
  { key: "creditOpex", label: "Crédito sobre despesas", color: IOS.mint, kind: "step" },
  { key: "tax", label: "Imposto líquido", color: IOS.orange, kind: "subtotal", moving: true },
  { key: "commission", label: "Comissão", color: IOS.purple, kind: "fixed" },
  { key: "opex", label: "Despesas operacionais", color: IOS.indigo, kind: "fixed" },
  { key: "margin", label: "Margem líquida", kind: "result", moving: true },
];

const Rail = styled.div`
  margin-top: 14px;
  display: grid;
  grid-template-columns: repeat(8, minmax(0, 1fr));
  gap: 2px;
`;

const YearButton = styled.button`
  padding: 6px 2px 7px;
  border: none;
  border-radius: 10px;
  background: ${({ $on, $preview, theme }) => ($on || $preview ? tokens.fill(theme) : "transparent")};
  box-shadow: ${({ $on, theme }) => ($on ? `inset 0 0 0 1px ${hairline(theme, true)}` : "none")};
  color: ${({ $on, theme }) => ($on ? theme.text : tokens.label2(theme))};
  font: inherit;
  font-variant-numeric: tabular-nums;
  text-align: center;
  cursor: pointer;
  transition: background 160ms ease, color 160ms ease;
  &:focus-visible { outline: none; box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.35); }

  .spark { display: flex; align-items: flex-end; justify-content: center; height: 18px; margin-bottom: 4px; }
  .spark i { display: block; width: 14px; min-height: 2px; border-radius: 2px 2px 1px 1px; }
  .y { display: block; font-size: 0.78em; font-weight: 600; letter-spacing: -0.015em; }
  .m { display: block; margin-top: 1px; font-size: 0.68em; font-weight: 500; opacity: 0.8; }
  @media (prefers-reduced-motion: reduce) { transition: none; }
`;

const Scroll = styled.div`
  margin-top: 12px;
  overflow-x: auto;
  scrollbar-width: thin;
`;

const Table = styled.div`
  min-width: 620px;
  font-variant-numeric: tabular-nums;
`;

const Line = styled.div`
  display: grid;
  grid-template-columns: ${COLUMNS};
  align-items: center;
  gap: 0;
  transition: background 140ms ease;

  > * { padding: 0 8px; }
  > :first-child { padding-left: 4px; }

  /* A coluna do ano escolhido corre a tabela inteira */
  .now { align-self: stretch; display: flex; align-items: center; justify-content: flex-end; background: ${({ theme }) => surface(theme, 1)}; }
  .num { text-align: right; white-space: nowrap; }
`;

const Head = styled(Line)`
  font-size: 0.7em;
  color: ${({ theme }) => tokens.label3(theme)};
  .now { padding-top: 7px; padding-bottom: 7px; border-radius: 8px 8px 0 0; font-size: 1.1em; font-weight: 600; color: ${({ theme }) => theme.text}; }
  .num { padding-top: 7px; padding-bottom: 7px; }
`;

const Row = styled(Line)`
  min-height: ${({ $kind }) => ($kind === "result" ? 58 : 42)}px;
  border-top: 1px solid ${({ theme, $kind }) => hairline(theme, $kind === "subtotal" || $kind === "result")};

  .name { display: flex; align-items: center; gap: 8px; min-width: 0; font-size: 0.84em; font-weight: ${({ $kind }) => ($kind === "step" || $kind === "fixed" ? 400 : 600)}; letter-spacing: -0.012em; line-height: 1.25; color: ${({ $kind, theme }) => ($kind === "step" || $kind === "fixed" ? tokens.label2(theme) : theme.text)}; }
  .name i { flex-shrink: 0; width: 6px; height: 6px; border-radius: 50%; }
  .name small { display: block; margin-top: 1px; font-size: 0.84em; font-weight: 300; letter-spacing: 0; color: ${label2}; }
  .today { font-size: 0.82em; color: ${label2}; }
  .now { font-size: ${({ $kind }) => ($kind === "result" ? "1.02em" : "0.84em")}; font-weight: ${({ $kind }) => ($kind === "step" || $kind === "fixed" ? 500 : 650)}; letter-spacing: -0.02em; white-space: nowrap; }
  .delta { font-size: 0.8em; font-weight: 550; letter-spacing: -0.015em; }
  &:last-child .now { border-radius: 0 0 8px 8px; }
`;

const Trend = styled.svg`
  display: block;
  width: 100%;
  height: 26px;
  overflow: visible;
  cursor: pointer;
`;

const Note = styled.p`
  margin: 8px 0 0;
  font-size: 0.72em;
  font-weight: 300;
  line-height: 1.45;
  color: ${label2};
`;

function RowTrend({ series, color, years, shownIndex, onHover, onPick, label }) {
  const W = 84;
  const H = 26;
  const min = Math.min(...series);
  const max = Math.max(...series);
  const flat = max - min < 0.005;
  const x = (i) => (i / (series.length - 1)) * (W - 8) + 4;
  const y = (v) => (flat ? H / 2 : 4 + ((max - v) / (max - min)) * (H - 8));
  const d = series.map((v, i) => `${i ? "L" : "M"}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(" ");
  return (
    <Trend viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" role="img" aria-label={`${label}, de 2026 a 2033`}>
      <path d={d} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" opacity="0.85" />
      <circle cx={x(shownIndex)} cy={y(series[shownIndex])} r="2.6" fill={color} />
      {years.map((yr, i) => (
        <rect
          key={yr.ano}
          x={(i / years.length) * W}
          y={0}
          width={W / years.length}
          height={H}
          fill="transparent"
          onPointerEnter={() => onHover(yr.ano)}
          onClick={() => onPick(yr.ano)}
        />
      ))}
    </Trend>
  );
}

export default function PriceBreakdown({ segmentId, inputs, params, price, delay }) {
  const theme = useTheme();
  const [selected, setSelected] = useState(2033);
  const [preview, setPreview] = useState(null);
  const data = useMemo(() => buildYearCascade(segmentId, inputs, params, price), [segmentId, inputs, params, price]);

  const rows = useMemo(() => {
    if (!data) return [];
    const valueOf = (key, row) => {
      if (key === "price") return data.price;
      if (key === "cost") return -data.cost;
      if (key === "tax") return -(row.taxRate / 100) * data.price;
      return row[key];
    };
    return ROWS
      .map((r) => ({
        ...r,
        today: valueOf(r.key, data.today),
        series: data.years.map((y) => valueOf(r.key, y)),
      }))
      .filter((r) => r.kind !== "step" || r.series.some((v) => Math.abs(v) >= 0.005));
  }, [data]);

  if (!data) {
    return (
      <Widget $delay={delay}>
        <div className="w-title">Para onde vai cada real do preço</div>
        <div className="w-sub">Informe um preço de venda para ver a conta.</div>
      </Widget>
    );
  }

  const { years, today } = data;
  const shownYear = preview ?? selected;
  const index = Math.max(0, years.findIndex((y) => y.ano === shownYear));
  const year = years[index];
  const marginMax = Math.max(1, ...years.map((v) => Math.abs(v.marginPct)));

  return (
    <Widget $delay={delay} style={{ display: "flex", flexDirection: "column" }}>
      <div className="w-title">Para onde vai cada real do preço</div>
      <div className="w-sub">Cobrando {money(data.price)}. Aponte um ano para comparar, e clique para fixar</div>

      <Rail role="radiogroup" aria-label="Ano da transição" onPointerLeave={() => setPreview(null)}>
        {years.map((v) => {
          const on = v.ano === selected;
          const height = Math.max(2, (Math.abs(v.marginPct) / marginMax) * 18);
          return (
            <YearButton
              key={v.ano}
              type="button"
              role="radio"
              aria-checked={on}
              aria-label={`${v.ano}: margem de ${pct(v.marginPct)}`}
              $on={on}
              $preview={v.ano === preview}
              onPointerEnter={() => setPreview(v.ano)}
              onClick={() => setSelected(v.ano)}
            >
              <span className="spark" aria-hidden="true"><i style={{ height, background: v.marginPct < 0 ? IOS.red : IOS.green, opacity: on ? 1 : 0.65 }} /></span>
              <span className="y">{v.ano}</span>
              <span className="m">{pct(v.marginPct)}</span>
            </YearButton>
          );
        })}
      </Rail>

      <Scroll onPointerLeave={() => setPreview(null)}>
        <Table role="table" aria-label={`Do preço à margem: hoje e em ${year.ano}`}>
          <Head role="row">
            <span>Do preço à margem</span>
            <span className="num">Hoje</span>
            <span className="now">{year.ano}{year.ano === THIS_YEAR ? " · hoje" : ""}</span>
            <span className="num">Efeito</span>
            <span className="num" title="De 2026 a 2033">Evolução</span>
          </Head>
          {rows.map((r) => {
            const value = r.series[index];
            const delta = value - r.today;
            const moved = Math.abs(delta) >= 0.005;
            const isLoss = r.kind === "result" && value < 0;
            const share = ((r.kind === "result" ? value : Math.abs(value)) / data.price) * 100;
            const trendColor = r.color || (r.kind === "result" ? (value < 0 ? IOS.red : IOS.green) : IOS.gray);
            const flat = Math.max(...r.series) - Math.min(...r.series) < 0.005;
            return (
              <Row key={r.key} role="row" $kind={r.kind}>
                <div className="name">
                  {r.color && <i style={{ background: r.color }} />}
                  <span>
                    {r.label}
                    {r.key !== "price" && <small>{pct(share)} do preço</small>}
                  </span>
                </div>
                <div className="num today" title={money(r.today)}>{Math.abs(r.today) < 0.005 && r.kind === "step" ? "—" : plainMoney(r.today)}</div>
                <div className="now" style={{ color: isLoss ? IOS.red : undefined }} title={money(value)}>
                  {Math.abs(value) < 0.005 && r.kind === "step" ? "—" : plainMoney(value)}
                </div>
                <div className="num delta" style={{ color: moved ? (delta < 0 ? IOS.red : IOS.green) : tokens.label3(theme) }} title={moved ? money(delta) : undefined}>
                  {signedMoney(delta)}
                </div>
                <div>
                  {!flat && (
                    <RowTrend
                      series={r.series}
                      color={trendColor}
                      years={years}
                      shownIndex={index}
                      label={r.label}
                      onHover={setPreview}
                      onPick={setSelected}
                    />
                  )}
                </div>
              </Row>
            );
          })}
        </Table>
      </Scroll>

      <PeriodStats style={{ marginTop: 14, gridTemplateColumns: "repeat(3, minmax(0, 1fr))" }}>
        <div>
          <span className="v">{pct(year.taxRate)}</span>
          <span className="l">Carga líquida · {pct(today.taxRate)} hoje</span>
        </div>
        <div>
          <span className="v">{data.multiplier.toLocaleString("pt-BR", { minimumFractionDigits: 4, maximumFractionDigits: 4 })}×</span>
          <span className="l">Multiplicador (Preço/Custo)</span>
        </div>
        <div>
          <span className="v">{data.divisor.toLocaleString("pt-BR", { minimumFractionDigits: 4, maximumFractionDigits: 4 })}</span>
          <span className="l">Divisor (Custo/Preço)</span>
        </div>
      </PeriodStats>

      <Note>
        Preço e custo mantidos em todos os anos. Cada ano aplica o cronograma da LC 214/2025 às alíquotas deste segmento.
        {year.ano === 2026 && " Em 2026, CBS e IBS são de teste e compensáveis, mas entram na conta, como na aba Transição."}
      </Note>
    </Widget>
  );
}
